import { useEffect, useState } from "react";
import dayjs from "dayjs";

/**
 * Custom hook to manage filters for cost data
 * @param {Array} costs - Complete list of costs
 * @param {Function} setFilteredCosts - Function to update filtered costs
 * @returns {Object} Filter state and functions
 */
const useFilters = (costs, setFilteredCosts) => {
  // Filter visibility state
  const [filterOpen, setFilterOpen] = useState(true);

  // Filter criteria state
  const [filters, setFilters] = useState({
    category: "",
    supplier: "",
    startDate: dayjs().startOf("month"),
    endDate: dayjs(),
    search: "",
    minValue: "",
    maxValue: "",
  });

  /**
   * Updates a single filter value
   * @param {string} name - Filter name
   * @param {any} value - Filter value
   */
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Resets all filters to default values
   */
  const resetFilters = () => {
    setFilters({
      category: "",
      supplier: "",
      startDate: dayjs().startOf("month"),
      endDate: dayjs(),
      search: "",
      minValue: "",
      maxValue: "",
    });
  };

  /**
   * Toggles the filter panel visibility
   */
  const toggleFilters = () => {
    setFilterOpen(!filterOpen);
  };

  /**
   * Applies current filters to the costs data
   * @param {Array} getCategoryName - Function to get category name from ID
   * @param {Function} getSupplierName - Function to get supplier name from ID
   */
  const applyFilters = (getCategoryName, getSupplierName) => {
    if (!costs || costs.length === 0) {
      setFilteredCosts([]);
      return;
    }

    let filtered = [...costs];

    // Apply category filter
    if (filters.category) {
      filtered = filtered.filter((cost) => cost.category === filters.category);
    }

    // Apply supplier filter
    if (filters.supplier) {
      filtered = filtered.filter((cost) => cost.supplier === filters.supplier);
    }

    // Apply date range filters
    if (filters.startDate) {
      filtered = filtered.filter((cost) => {
        const costDate = dayjs(cost.date);
        return (
          costDate.isAfter(filters.startDate, "day") ||
          costDate.isSame(filters.startDate, "day") ||
          cost.date === filters.startDate.format("YYYY-MM-DD")
        );
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter((cost) => {
        const costDate = dayjs(cost.date);
        return (
          costDate.isBefore(filters.endDate, "day") ||
          costDate.isSame(filters.endDate, "day") ||
          cost.date === filters.endDate.format("YYYY-MM-DD")
        );
      });
    }

    // Apply value range filters
    if (filters.minValue) {
      filtered = filtered.filter(
        (cost) => cost.value >= parseFloat(filters.minValue)
      );
    }

    if (filters.maxValue) {
      filtered = filtered.filter(
        (cost) => cost.value <= parseFloat(filters.maxValue)
      );
    }

    // Apply search filter (most complex filter)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((cost) => {
        // Get related names for searching
        const categoryName = getCategoryName(cost.category).toLowerCase();
        const supplierName = getSupplierName(cost.supplier).toLowerCase();
        const invoiceNumber = (cost.invoice_number || "").toLowerCase();
        const notes = (cost.notes || "").toLowerCase();

        // Search in all relevant fields
        return (
          categoryName.includes(searchLower) ||
          supplierName.includes(searchLower) ||
          invoiceNumber.includes(searchLower) ||
          notes.includes(searchLower)
        );
      });
    }

    setFilteredCosts(filtered);
  };

  // Auto-apply filters when costs or filter criteria change
  useEffect(() => {
    if (costs && costs.length > 0) {
      // Note: getCategoryName and getSupplierName need to be passed from outside
      // as they depend on categories and suppliers data not available in this hook
    }
  }, [costs, filters]);

  return {
    filters,
    filterOpen,
    handleFilterChange,
    resetFilters,
    toggleFilters,
    applyFilters,
  };
};

export default useFilters;
